import type {ReactNode} from 'react';

import {RightOutlined} from '@ant-design/icons';
import {useState} from 'react';

import {cn} from '../../helpers/cn';

export type CollapseItem = {
  key: string;
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
};

export type CollapseProps = {
  items?: CollapseItem[];
  activeKey?: string | string[];
  defaultActiveKey?: string | string[];
  accordion?: boolean;
  bordered?: boolean;
  className?: string;
  onChange?: (key: string | string[]) => void;
  children?: ReactNode;
};

function normalizeKeys(value?: string | string[]): string[] {
  if (value == null) return [];
  return Array.isArray(value) ? value.map(String) : [String(value)];
}

export function Collapse({
  items,
  activeKey,
  defaultActiveKey,
  accordion = false,
  bordered = true,
  className,
  onChange
}: CollapseProps) {
  const isControlled = activeKey !== undefined;
  const [uncontrolledKeys, setUncontrolledKeys] = useState(normalizeKeys(defaultActiveKey));
  const openKeys = isControlled ? normalizeKeys(activeKey) : uncontrolledKeys;

  const toggle = (key: string) => {
    let next: string[];
    const isOpen = openKeys.includes(key);

    if (accordion) {
      next = isOpen ? [] : [key];
    } else if (isOpen) {
      next = openKeys.filter((k) => k !== key);
    } else {
      next = [...openKeys, key];
    }

    if (!isControlled) {
      setUncontrolledKeys(next);
    }
    onChange?.(accordion ? (next[0] ?? '') : next);
  };

  if (!items?.length) return null;

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-[8px] bg-gray-02 font-iransans',
        bordered && 'border border-disabled-border',
        className
      )}
    >
      {items.map((item, index) => {
        const open = openKeys.includes(String(item.key));
        return (
          <div
            key={item.key}
            className={cn(index > 0 && bordered && 'border-t border-disabled-border')}
          >
            <button
              type="button"
              disabled={item.disabled}
              aria-expanded={open}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-3 text-start text-[14px] text-neutral-fg cursor-pointer',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-orange',
                'disabled:cursor-not-allowed disabled:text-disabled-fg'
              )}
              onClick={() => {
                if (!item.disabled) toggle(String(item.key));
              }}
            >
              <span>{item.label}</span>
              <RightOutlined
                aria-hidden
                className={cn(
                  'shrink-0 text-[10px] text-gray-30 transition-transform duration-300',
                  open ? 'rotate-90' : 'rotate-180'
                )}
              />
            </button>
            <div
              className={cn(
                'grid transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.645,0.045,0.355,1)]',
                open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="border-t border-disabled-border bg-white p-4 text-[14px] text-neutral-fg">
                  {item.children}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export type CollapsePanelProps = {
  header: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
};
